import { useState, useEffect } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { Checkbox } from '@progress/kendo-react-inputs'

import {
  ERRORS,
  EXPLANATIONS,
  FORM,
  PERMISSIONS,
  PLACEHOLDERS,
  ROLE_EXPLANATIONS,
  ROLES,
  TITLES,
} from 'locales/locales'
import {
  useLazyGetRolePermissionsQuery,
  useAddPermissionToRoleMutation,
} from 'app/services/system.service'
import {
  IRolePermissions,
  SettingsPermissions,
  MessagingPermissions,
  UserManagementPermissions,
  NewsFeedPermissions,
  VideoConferencePermissions,
  TeamAndChannelPermissions,
  PermissionType,
  AdminPermissions,
} from 'types/role.types'
import Dropdown from 'components/kendo/dropdown/DropdownList'
import SearchInput from 'components/input/search-input/SearchInput'
import CustomText from 'components/text/CustomText'
import { icons } from 'utils/icons'
import CustomIcon from 'components/icon/CustomIcon'
import Tooltip from 'components/kendo/tooltips/Tooltip'

interface Role {
  _id: string
  name: string
}

interface Permission {
  _id: string
  value: number
  name: string
  type: number
  typeName: string
  isSelected: boolean
  explanation: string
}

interface PermissionManagementProps {
  selectedRole: Role | null
}

interface PermissionsByCategory {
  [categoryName: string]: Permission[]
}

interface CalculatedPermissions {
  settings: number
  messaging: number
  newsFeed: number
  teamAndChannel: number
  videoConference: number
  userManagement: number
  admin: number
}

const PERMISSION_TYPE_MAPPING = [
  { type: PermissionType.SETTINGS_PERMISSIONS, enum: SettingsPermissions },
  { type: PermissionType.NEWS_FEED_PERMISSIONS, enum: NewsFeedPermissions },
  { type: PermissionType.MESSAGING_PERMISSIONS, enum: MessagingPermissions },
  { type: PermissionType.VIDEO_CONFERENCE_PERMISSIONS, enum: VideoConferencePermissions },
  { type: PermissionType.TEAM_AND_CHANNEL_PERMISSIONS, enum: TeamAndChannelPermissions },
  { type: PermissionType.USER_MANAGEMENT_PERMISSIONS, enum: UserManagementPermissions },
  { type: PermissionType.ADMIN_PERMISSIONS, enum: AdminPermissions },
]

const PermissionManagement = ({ selectedRole }: PermissionManagementProps) => {
  const { t } = useTranslation()

  const [allPermissions, setAllPermissions] = useState<Permission[]>([])
  const [selectedCategory, setSelectedCategory] = useState<string>(t(TITLES.PERMISSIONS_TITLE))
  const [searchTerm, setSearchTerm] = useState<string>('')

  const [getRolePermissions] = useLazyGetRolePermissionsQuery()
  const [updateRolePermissions] = useAddPermissionToRoleMutation()

  useEffect(() => {
    if (selectedRole) {
      loadRolePermissions()
    }
  }, [selectedRole])

  const loadRolePermissions = async () => {
    if (!selectedRole) return

    try {
      const response = await getRolePermissions(selectedRole._id)
      const permissions = response.data?.data?.permissions
      if (permissions) {
        const processedPermissions = processPermissions(permissions)
        setAllPermissions(processedPermissions)
      }
    } catch (error) {
      console.error('Error loading role permissions:', error)
    }
  }

  const processPermissions = (rolePermissions: IRolePermissions): Permission[] => {
    const allPermissions: Permission[] = []

    PERMISSION_TYPE_MAPPING.forEach(({ type, enum: permissionEnum }) => {
      const categoryPermissions = rolePermissions[getPermissionKey(type)] ?? 0
      const permissions = createPermissionsFromEnum(type, permissionEnum, categoryPermissions)
      allPermissions.push(...permissions)
    })

    return allPermissions
  }

  const createPermissionsFromEnum = (
    permissionType: PermissionType,
    permissionEnum: any,
    rolePermissions: number,
  ): Permission[] => {
    const permissions: Permission[] = []
    const typeName = t(PERMISSIONS[PermissionType[permissionType]])

    Object.keys(permissionEnum).forEach((key) => {
      if (isNaN(Number(key))) {
        const value = permissionEnum[key]
        const isSelected = (rolePermissions & value) === value
        permissions.push({
          _id: `${key}-${value}`,
          name: t(PERMISSIONS[key]),
          value,
          type: permissionType,
          typeName,
          isSelected,
          explanation: t(ROLE_EXPLANATIONS[PERMISSIONS[key]]),
        })
      }
    })

    return permissions
  }

  const getPermissionKey = (type: PermissionType): keyof IRolePermissions => {
    const mapping: Record<PermissionType, keyof IRolePermissions> = {
      [PermissionType.SETTINGS_PERMISSIONS]: 'settings',
      [PermissionType.MESSAGING_PERMISSIONS]: 'messaging',
      [PermissionType.NEWS_FEED_PERMISSIONS]: 'newsFeed',
      [PermissionType.TEAM_AND_CHANNEL_PERMISSIONS]: 'teamAndChannel',
      [PermissionType.VIDEO_CONFERENCE_PERMISSIONS]: 'videoConference',
      [PermissionType.USER_MANAGEMENT_PERMISSIONS]: 'userManagement',
      [PermissionType.ADMIN_PERMISSIONS]: 'admin',
    }
    return mapping[type]
  }

  const handlePermissionToggle = async (permission: Permission, isChecked: boolean) => {
    if (!selectedRole) return

    const updatedPermissions = allPermissions.map((p) =>
      p._id === permission._id ? { ...p, isSelected: isChecked } : p,
    )
    setAllPermissions(updatedPermissions)

    const calculatedPermissions = calculateRolePermissions(updatedPermissions)
    await updateRolePermissions({
      _id: selectedRole._id,
      ...calculatedPermissions,
    })
  }

  const handleCategoryToggle = async (categoryName: string, isChecked: boolean) => {
    if (!selectedRole) return

    const categoryPermissions = permissionsByCategory[categoryName] || []
    const categoryPermissionIds = categoryPermissions.map((p) => p._id)

    const updatedPermissions = allPermissions.map((p) =>
      categoryPermissionIds.includes(p._id) ? { ...p, isSelected: isChecked } : p,
    )
    setAllPermissions(updatedPermissions)

    const calculatedPermissions = calculateRolePermissions(updatedPermissions)
    await updateRolePermissions({
      _id: selectedRole._id,
      ...calculatedPermissions,
    })
  }

  const handleMasterToggle = async (isChecked: boolean) => {
    if (!selectedRole) return

    const updatedPermissions = allPermissions.map((p) => ({ ...p, isSelected: isChecked }))
    setAllPermissions(updatedPermissions)

    const calculatedPermissions = calculateRolePermissions(updatedPermissions)
    await updateRolePermissions({
      _id: selectedRole._id,
      ...calculatedPermissions,
    })
  }

  const isCategorySelected = (categoryName: string): boolean => {
    const categoryPermissions = permissionsByCategory?.[categoryName] || []
    return categoryPermissions.length > 0 && categoryPermissions.every((p) => p.isSelected)
  }

  const isMasterSelected = (): boolean => {
    return allPermissions.length > 0 && allPermissions.every((p) => p.isSelected)
  }

  const calculateRolePermissions = (permissions: Permission[]): CalculatedPermissions => {
    const selectedPermissions = permissions.filter((p) => p.isSelected)

    return selectedPermissions.reduce(
      (acc, permission) => {
        const key = getPermissionKey(permission.type)
        acc[key] |= permission.value
        return acc
      },
      {
        settings: 0,
        messaging: 0,
        newsFeed: 0,
        teamAndChannel: 0,
        videoConference: 0,
        userManagement: 0,
        admin: 0,
      },
    )
  }

  const permissionsByCategory: PermissionsByCategory = allPermissions.reduce((acc, permission) => {
    if (!acc[permission.typeName]) {
      acc[permission.typeName] = []
    }
    acc[permission.typeName].push(permission)
    return acc
  }, {} as PermissionsByCategory)

  const categoryNames = Object.keys(permissionsByCategory)
  const dropdownOptions = [
    { title: t(TITLES.PERMISSIONS_TITLE), value: t(TITLES.PERMISSIONS_TITLE) },
    ...categoryNames.map((name) => ({ title: name, value: name })),
  ]

  const isShowingAllCategories = selectedCategory === t(TITLES.PERMISSIONS_TITLE)
  const currentCategoryPermissions = isShowingAllCategories
    ? []
    : permissionsByCategory[selectedCategory] || []

  // Filter permissions based on search term
  const filterPermissionsBySearch = (permissions: Permission[]): Permission[] => {
    if (!searchTerm.trim()) return permissions
    return permissions.filter((permission) =>
      permission.name.toLowerCase().includes(searchTerm.toLowerCase()),
    )
  }

  const filterCategoryBySearch = (categoryPermissions: Permission[]): Permission[] => {
    return filterPermissionsBySearch(categoryPermissions)
  }

  const renderPermissionCheckbox = (permission: Permission, index: number) => {
    return (
      <PermissionItem
        key={permission._id}
        isOdd={index % 2 === 1}
        onClick={() => handlePermissionToggle(permission, !permission.isSelected)}
      >
        <PermissionItemWrapper>
          <Checkbox
            checked={permission.isSelected}
            onChange={(e) => handlePermissionToggle(permission, e.value)}
          />
          <CustomText typography="body3">{permission?.name || permission?._id}</CustomText>
          <Tooltip
            content={permission.name}
            anchorElement="target"
            position="top"
            openDelay={0}
            parentTitle={false}
            style={{
              maxWidth: '200px',
            }}
          >
            <StyledInfoIcon
              source={icons.infoCircleIcon}
              title={permission?.explanation || permission?._id}
            />
          </Tooltip>
        </PermissionItemWrapper>
      </PermissionItem>
    )
  }

  const renderCategorySection = (categoryName: string, permissions: Permission[]) => {
    const filteredPermissions = filterCategoryBySearch(permissions)

    if (filteredPermissions.length === 0) return null

    const isSelected = isCategorySelected(categoryName)

    return (
      <CategorySection key={categoryName}>
        <CategoryTitleContainer>
          <Checkbox
            checked={isSelected}
            onChange={(e) => handleCategoryToggle(categoryName, e.value)}
          />
          <CustomText typography="header3">{categoryName}</CustomText>
        </CategoryTitleContainer>
        {filteredPermissions.map((permission, index) =>
          renderPermissionCheckbox(permission, index),
        )}
      </CategorySection>
    )
  }

  const renderPermissions = () => {
    if (!selectedRole) {
      return <EmptyMessage>{t(FORM.SELECT_ROLE)}</EmptyMessage>
    }

    if (isShowingAllCategories) {
      const renderedCategories = Object.entries(permissionsByCategory)
        .map(([categoryName, permissions]) => renderCategorySection(categoryName, permissions))
        .filter(Boolean)

      return renderedCategories.length > 0 ? (
        renderedCategories
      ) : (
        <EmptyMessage>{t(ERRORS.NO_SEARCH_RESULTS)}</EmptyMessage>
      )
    }

    if (selectedCategory && currentCategoryPermissions.length > 0) {
      const filteredPermissions = filterCategoryBySearch(currentCategoryPermissions)

      if (filteredPermissions.length === 0) {
        return <EmptyMessage>{t(ERRORS.NO_SEARCH_RESULTS)}</EmptyMessage>
      }

      return renderCategorySection(selectedCategory, currentCategoryPermissions)
    }

    return <EmptyMessage>{EXPLANATIONS.NO_CATEGORY_PERMISSIONS}</EmptyMessage>
  }

  return (
    <Container>
      <TitleContainer>
        <TitleLeft>
          <StyledCustomText typography="header1">{t(TITLES.PERMISSIONS_TITLE)}</StyledCustomText>
        </TitleLeft>
        {selectedRole && allPermissions.length > 0 && (
          <TitleRight>
            <MasterCheckboxContainer>
              <Checkbox
                checked={isMasterSelected()}
                onChange={(e) => handleMasterToggle(e.value)}
                label={t(ROLES.SELECT_ALL)}
              />
            </MasterCheckboxContainer>
          </TitleRight>
        )}
      </TitleContainer>
      <FilterContainer>
        <FilterLeft>
          <StyledCustomText typography="header3">{t(FORM.FILTER)}:</StyledCustomText>
          <Dropdown
            id="permission-category-dropdown"
            selectedOption={{ title: selectedCategory, value: selectedCategory }}
            style={{ width: '200px' }}
            options={dropdownOptions}
            onChangeOption={(option) => setSelectedCategory(option.value)}
          />
        </FilterLeft>
        <FilterRight>
          <SearchInput
            placeHolder={t(PLACEHOLDERS.SEARCH_PLACEHOLDER)}
            value={searchTerm}
            onChange={(value) => setSearchTerm(value)}
          />
        </FilterRight>
      </FilterContainer>
      <Content>
        <PermissionsContainer>{renderPermissions()}</PermissionsContainer>
      </Content>
    </Container>
  )
}

export default PermissionManagement

const StyledCustomText = styled(CustomText)`
  word-wrap: normal;
  white-space: nowrap;
  @media (max-width: 1024px) {
    font-size: 12px;
  }
`

const Container = styled.div`
  flex: 0 0 35%;
  border: 1px solid #e3ebf6;
  border-radius: 4px;
  overflow: hidden;

  @media (max-width: 1024px) {
    flex: none;
    min-height: 400px;
  }
`

const TitleContainer = styled.div`
  padding: 10px 14px;
  background-color: #f8f9fa;
  border-bottom: 1px solid #e3ebf6;
  font-weight: 600;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  @media (max-width: 1024px) {
    flex-direction: column;
    gap: 8px;
    padding: 16px;
  }
`

const TitleLeft = styled.div`
  display: flex;
  align-items: center;
`

const TitleRight = styled.div`
  display: flex;
  align-items: center;
`

const MasterCheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 500;
`

const FilterContainer = styled.div`
  padding: 12px 16px;
  background-color: #f8f9fa;
  border-bottom: 1px solid #e3ebf6;
  font-weight: 600;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 1024px) {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
    padding: 16px;
  }
`

const FilterLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: 1024px) {
    justify-content: space-between;
    margin-bottom: 8px;
  }
`

const FilterRight = styled.div`
  flex: 0 0 150px;

  @media (max-width: 1024px) {
    flex: none;
    width: 100%;
  }
`

const Content = styled.div`
  padding: 16px;
  height: calc(100% - 96px);
  overflow-y: auto;
`

const PermissionsContainer = styled.div`
  height: 100%;
  overflow-y: auto;
`

const CategorySection = styled.div`
  margin-bottom: 24px;
`

const CategoryTitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  padding-bottom: 4px;
  border-bottom: 1px solid #e3ebf6;
`

const PermissionItemWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`

const PermissionItem = styled.div<{ isOdd?: boolean }>`
  margin-bottom: 3px;
  padding: 8px;
  background-color: ${(props) => (props.isOdd ? '#eceff3' : 'transparent')};
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${(props) => (props.isOdd ? '#c0c2c4' : '#bcbec0')};
  }
`

const StyledInfoIcon = styled(CustomIcon)`
  cursor: pointer;
  width: 20px;
  height: 20px;
  display: flex;
`

const EmptyMessage = styled.div`
  text-align: center;
  color: #666;
  font-style: italic;
  margin-top: 50px;
`
